import {
  Component,
  inject,
  Renderer2,
  OnInit,
  OnDestroy,
  ViewChild,
  effect,
  ChangeDetectorRef,
} from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { AvatarModule } from 'primeng/avatar';
import { DialogService } from 'primeng/dynamicdialog';
import { usePreset } from '@primeuix/themes';
import SpringPreset from '../shared/themes/spring.preset';
import VividPreset from '../shared/themes/vivid.preset';
import { KeyboardService, MessengerUnreadService } from '../core/services';
import { NavbarComponent } from './navbar/navbar.component';
import { MobileHeaderComponent } from './mobile-header/mobile-header.component';
import { AppsDialogComponent } from '../features/dialogs';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterOutlet,
    CardModule,
    ButtonModule,
    ButtonGroupModule,
    DialogModule,
    DynamicDialogModule,
    NavbarComponent,
    MobileHeaderComponent,
    AvatarModule,
    TooltipModule,
    TranslocoPipe,
  ],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  providers: [DialogService],
})
export class LayoutComponent implements OnInit, OnDestroy {
  private keyboardService = inject(KeyboardService);
  private renderer = inject(Renderer2);
  private router = inject(Router);
  private dialogService = inject(DialogService);
  private cdr = inject(ChangeDetectorRef);
  private translocoService = inject(TranslocoService);
  messengerUnreadService = inject(MessengerUnreadService);

  private scrollSubscription?: Subscription;
  activeMenuItem = 'smart-org';
  showMobileHeader = true;
  isAiAgentRoute = false;

  navItems = [
    { route: 'ai-agent', label: 'navbar.aiChat', labelKey: 'navbar.aiChat', iconClass: 'fa-light fa-robot', disable: false },
    { route: 'messenger', label: 'navbar.orgChat', labelKey: 'navbar.orgChat', iconClass: 'fa-light fa-messages', disable: false },
    { route: 'calendar', label: 'navbar.calendar', labelKey: 'navbar.calendar', iconClass: 'fa-light fa-calendar', disable: true },
    { route: 'planner', label: 'navbar.planner', labelKey: 'navbar.planner', iconClass: 'fa-light fa-list-check', disable: true },
    { route: 'jobs', label: 'navbar.jobs', labelKey: 'navbar.jobs', iconClass: 'fa-light fa-briefcase', disable: true },
  ];

  // Get unread counts from service
  get messengerUnreadMessages(): number {
    return this.messengerUnreadService.unreadMessages();
  }

  get messengerUnreadMentions(): number {
    return this.messengerUnreadService.unreadMentions();
  }

  get shouldShowMessengerBadge(): boolean {
    return this.messengerUnreadService.shouldShowBadge();
  }

  getMessengerTooltip(): string {
    const orgChatLabel = this.translocoService.translate('navbar.orgChat');
    const unreadLabel = this.translocoService.translate('navbar.unread');
    return `<div style="font-weight: 600; margin-bottom: 4px;">${orgChatLabel}</div>
      <div style="font-size: 0.875rem;" class="text-center">${this.messengerUnreadMessages} ${unreadLabel} </div>
      <div style="font-size: 0.875rem;" class="text-center text-rose-400/80">${this.messengerUnreadMentions} @</div>`;
  }

  ngOnInit() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateActiveNavItem();
    });

    this.updateActiveNavItem();
    this.checkScreenSize();
    this.setupResizeListener();
  }

  ngOnDestroy() {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
    window.removeEventListener('resize', this.onResize);
  }

  updateActiveNavItem() {
    this.activeMenuItem = this.router.url;
    // Hide mobile header on ai-agent routes
    this.showMobileHeader = !this.router.url.includes('ai-agent');
    this.isAiAgentRoute = this.router.url.includes('ai-agent');
  }

  navigateToRoute(route: string) {
    this.router.navigate([route]);
    this.activeMenuItem = route;
  }

  toggleAppsDialog() {
    this.dialogService.open(AppsDialogComponent, {
      header: this.translocoService.translate('navbar.availableApps'),
      width: '80vw',
      styleClass: 'apps-dialog',
      modal: true,
      dismissableMask: true,
      closable: false,
      showHeader: false,
    });
  }

  setSprintTheme() {
    usePreset(SpringPreset);
    const element = document.querySelector('html');
    element.classList.remove('vivid-theme');
    element.classList.add('sprint-theme');
  }

  setVividTheme() {
    usePreset(VividPreset);
    const element = document.querySelector('html');
    element.classList.remove('sprint-theme');
    element.classList.add('vivid-theme');
  }

  private checkScreenSize() {
    // Hide mobile header on wide screens (lg breakpoint is 1024px)
    const isWideScreen = window.innerWidth >= 1024;
    if (isWideScreen) {
      this.showMobileHeader = false;
    } else {
      // Only show if not on ai-agent routes
      this.showMobileHeader = !this.router.url.includes('ai-agent');
    }
    this.cdr.detectChanges();
  }

  private setupResizeListener() {
    this.onResize = () => {
      this.checkScreenSize();
    };
    window.addEventListener('resize', this.onResize);
  }

  private onResize = () => {
    // This will be set in setupResizeListener
  };

  toggleBottomBar() {
    const bottomBar = document.querySelector('.bottom-bar');
    if (bottomBar) {
      bottomBar.classList.toggle('hidden-bottom-bar');
    }
  }
}
